import React, {useEffect, useState, useRef} from 'react';
import {extendTheme} from '@mui/material/styles';
import TableChartIcon from '@mui/icons-material/TableChart';
import PeopleIcon from '@mui/icons-material/People';
import TimelineIcon from '@mui/icons-material/Timeline';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BarChartIcon from '@mui/icons-material/BarChart';
import {AppProvider} from '@toolpad/core/AppProvider';
import {DashboardLayout} from '@toolpad/core/DashboardLayout';
import {Routes, Route, useNavigate, useLocation, Navigate} from 'react-router-dom';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Home from './components/Home';
import ProductTable from './components/ProductTable';
import CustomerTable from './components/CustomerTable';
import CustomerIntegrationResolutionPage from './components/CustomerIntegrationResolutionPage';
import CustomerForecast from './components/CustomerForecast';
import ForecastOutputsSummary from './components/ForecastOutputsSummary';
import ForecastOutputsPromotions from './components/ForecastOutputsPromotions';
import ForecastOutputsSales from './components/ForecastOutputsSales.jsx';
import ForecastOutputsAccount from './components/ForecastOutputsAccount';
import ForecastAccuracy from './components/ForecastAccuracy';
import ProfilePage from './components/ProfilePage';
import MfaChallenge from './components/MfaChallenge';
import MfaSetup from './components/MfaSetup';
import ThirdPartyIntegrationsPage from './components/ThirdPartyIntegrationsPage';
import SynchronizationPage from './components/SynchronizationPage';
import AdminPage from './components/AdminPage';
import ImportPage from './components/ImportPage';
import ProtectedRoute from './ProtectedRoute';
import {AuthProvider, useAuth} from './AuthContext';
import Typography from '@mui/material/Typography';
import axiosInstance from "./AxiosInstance";
import {CardGiftcard, Layers, Person2} from "@mui/icons-material";
import SyncIcon from '@mui/icons-material/Sync';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Box from "@mui/material/Box";
import usePageTracking from './hooks/usePageTracking';
import ReactGA from 'react-ga4';
import CustomToolbarActions from './components/CustomToolbarActions';

const NAVIGATION = [
    {
        kind: 'header',
        title: 'Main Items',
    },
    {
        segment: '',
        title: 'Dashboard',
        icon: <DashboardIcon/>,
    },
    {
        segment: 'sales-tracker',
        title: 'Sales Tracker',
        icon: <BarChartIcon/>,
    },
    {
        segment: 'forecasting',
        title: 'Forecasting',
        icon: <TimelineIcon/>,
    },
    {
        kind: 'divider',
    },
    {
        kind: 'header',
        title: 'Analytics',
    },
    {
        segment: 'summary',
        title: 'Summary',
        icon: <TableChartIcon/>,
    },
    {
        segment: 'p-and-l',
        title: 'P&L',
        icon: <TableChartIcon/>,
    },
    {
        segment: 'forecast-accuracy',
        title: 'Forecast Accuracy',
        icon: <TableChartIcon/>,
    },
    {
        segment: 'promotion-laydown',
        title: 'Promotion Laydown',
        icon: <TableChartIcon/>,
    },
    {
        kind: 'divider',
    },
    {
        kind: 'header',
        title: 'Settings',
    },
    {
        segment: 'settings/synchronization',
        title: 'Sync',
        icon: <SyncIcon/>,
    },
    {
        segment: 'settings/import',
        title: 'Import',
        icon: <CloudUploadIcon/>,
    },
    {
        segment: 'settings/integrations',
        title: 'Integrations',
        icon: <Layers/>,
    },
    {
        segment: 'products',
        title: 'Products',
        icon: <CardGiftcard/>,
    },
    {
        segment: 'customers',
        title: 'Customers',
        icon: <PeopleIcon/>,
    },
    {
        segment: 'settings/profile',
        title: 'Profile',
        icon: <Person2/>,
    },
];

const theme = extendTheme({
    colorSchemes: {light: true, dark: true},
    colorSchemeSelector: 'class',
    shape: {
        borderRadius: 12,
    },
    typography: {
        fontFamily: [
            'Inter',
            'system-ui',
            '-apple-system',
            'Segoe UI',
            'Roboto',
            'Helvetica',
            'Arial',
            'sans-serif',
        ].join(','),
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 600,
            lg: 1200,
            xl: 1536,
        },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: 'var(--mui-palette-background-default)',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    borderRight: '1px solid',
                    borderColor: 'var(--mui-palette-divider)',
                    width: 260,
                    boxSizing: 'border-box',
                    '& .MuiListItemButton-root': {
                        height: 34,
                        minHeight: 34,
                        paddingTop: 0,
                        paddingBottom: 0,
                    },
                    '& .MuiListItemButton-root .MuiListItemIcon-root': {
                        minWidth: 28,
                    },
                },
            },
        },
        MuiList: {
            styleOverrides: {
                root: {
                    paddingTop: 8,
                    paddingBottom: 8,
                },
            },
        },
        MuiListSubheader: {
            styleOverrides: {
                root: {
                    lineHeight: 1.2,
                    paddingTop: 10,
                    paddingBottom: 2,
                    paddingLeft: 14,
                    paddingRight: 14,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.8,
                    textTransform: 'uppercase',
                    color: 'var(--mui-palette-text-secondary)',
                    backgroundColor: 'transparent',
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    minWidth: 28,
                    color: 'inherit',
                    '& svg': {
                        fontSize: 18,
                    },
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    marginLeft: 2,
                    marginRight: 2,
                    borderRadius: 8,
                    paddingTop: 6,
                    paddingBottom: 6,
                    gap: 4,
                    minHeight: 32,
                    transition: 'background-color 120ms ease, box-shadow 120ms ease',
                    '&:hover': {
                        backgroundColor: 'var(--mui-palette-action-hover)',
                    },
                    '&.Mui-selected, &.Mui-selected:hover': {
                        backgroundColor: 'var(--mui-palette-action-selected)',
                        boxShadow: 'inset 0 0 0 1px var(--mui-palette-divider)',
                    },
                },
            },
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    fontWeight: 500,
                    fontSize: 15,
                    lineHeight: 1.15,
                },
            },
        },
        MuiTablePagination: {
            styleOverrides: {
                root: {
                    '& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel': {
                        margin: 0,
                        alignSelf: 'center',
                        lineHeight: 1,
                    },
                    '& .MuiTablePagination-toolbar': {
                        alignItems: 'center',
                    },
                },
            },
        },
    },
});

function useCustomRouter() {
    const navigate = useNavigate();
    const location = useLocation();

    return React.useMemo(() => ({
        pathname: location.pathname,
        searchParams: new URLSearchParams(location.search),
        navigate: (path) => navigate(path),
    }), [location, navigate]);
}

function SidebarFooter({mini}) {
    const [versionInfo, setVersionInfo] = useState({buildNumber: ''});

    useEffect(() => {
        axiosInstance.get('/info')
            .then(response => {
                setVersionInfo({
                    buildNumber: response.data.buildNumber,
                });
            })
            .catch(error => {
                console.error('Error fetching version info:', error);
            });
    }, []);

    return (
        <Box
            sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 1,
                textAlign: 'center',
            }}
        >
            <Typography
                variant="caption"
                sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
                {mini
                    ? '© AIQ'
                    : `© ${new Date().getFullYear()} AbakusIQ Ltd | v${versionInfo.buildNumber || '—'}`}
            </Typography>
        </Box>
    );
}

function AppWithNavigation() {

    usePageTracking();

    const router = useCustomRouter();
    const navigate = useNavigate();
    const location = useLocation();
    const {isAuthenticated, handleLogout, organisation, email} = useAuth();
    const toolbarActionsRef = useRef();
    const sessionDisplayName = organisation && organisation !== email ? organisation : '';
    const isPublicRoute = ['/login', '/forgot-password', '/reset-password', '/login/mfa-challenge', '/login/mfa-setup'].includes(location.pathname);

    const [session, setSession] = React.useState({
        user: {
            name: sessionDisplayName,
            email: email || '',
        },
    });

    const [featureFlags, setFeatureFlags] = useState({
        enableThirdPartyIntegration: false,
    });

    const [navigation, setNavigation] = useState([]);

    useEffect(() => {
        if (email) {
            setSession({
                user: {
                    name: sessionDisplayName,
                    email: email,
                },
            });
        } else {
            setSession(null);
        }
    }, [email, sessionDisplayName]);

    useEffect(() => {
        if (!isAuthenticated) return;

        axiosInstance.get('/info/features')
            .then(response => {
                const { enableThirdPartyIntegration } = response.data;

                const nav = NAVIGATION.filter(item => {
                    if ((item.segment === 'sales-tracker' || item.segment === 'settings/integrations' || item.segment === 'settings/synchronization') && !enableThirdPartyIntegration) {
                        return false;
                    }
                    return true;
                });

                setFeatureFlags({ enableThirdPartyIntegration });
                setNavigation(nav);
            })
            .catch(error => {
                console.warn('Feature flag check failed:', error);
                setNavigation(NAVIGATION); // fallback
            });
    }, [isAuthenticated]);

    const authentication = React.useMemo(() => {
        return {
            signIn: () => {},
            signOut: () => {
                setSession(null);
                handleLogout();
            },
        };
    }, [handleLogout]);

    const ToolbarActionsWithAuth = React.useMemo(() => {
        return React.forwardRef((props, ref) => 
            <CustomToolbarActions 
                ref={ref} 
                isAuthenticated={isAuthenticated} 
                enableThirdPartyIntegration={featureFlags.enableThirdPartyIntegration}
            />
        );
    }, [isAuthenticated, featureFlags.enableThirdPartyIntegration]);

    const refreshNotifications = React.useCallback(() => {
        if (toolbarActionsRef.current) {
            toolbarActionsRef.current.refreshNotifications();
        }
    }, []);

    React.useEffect(() => {
        window.refreshNotifications = refreshNotifications;
        return () => {
            delete window.refreshNotifications;
        };
    }, [refreshNotifications]);

    return (
        <AppProvider
            session={isPublicRoute ? null : session}
            router={router}
            authentication={isPublicRoute ? undefined : authentication}
            navigation={navigation}
            branding={{title: 'AbakusIQ'}}
            theme={theme}
        >
            <DashboardLayout
                hideNavigation={!isAuthenticated}
                slots={{
                    sidebarFooter: SidebarFooter,
                    toolbarActions: (props) => <ToolbarActionsWithAuth ref={toolbarActionsRef} {...props} />,
                }}
            >
                <Routes>
                    {/* Public Routes */}
                    <Route path="/login" element={<LoginPage/>}/>
                    <Route path="/login/mfa-challenge" element={<MfaChallenge/>}/>
                    <Route path="/login/mfa-setup" element={<MfaSetup/>}/>
                    <Route path="/forgot-password" element={<ForgotPassword/>}/>
                    <Route path="/reset-password" element={<ResetPassword/>}/>

                    {/* Protected Routes */}
                    <Route element={<ProtectedRoute/>}>
                        <Route path="/" element={<Home/>}/>
                        <Route path="/products" element={<ProductTable/>}/>
                        <Route path="/customers" element={<CustomerTable/>}/>
                        <Route path="/customers/incoming" element={<CustomerIntegrationResolutionPage/>}/>
                        <Route path="/forecasting/:id?" element={<CustomerForecast/>}/>
                        <Route path="/summary" element={<ForecastOutputsSummary/>}/>
                        <Route path="/p-and-l" element={<ForecastOutputsAccount/>}/>
                        <Route path="/promotion-laydown" element={<ForecastOutputsPromotions/>}/>
                        <Route path="/forecast-accuracy" element={<ForecastAccuracy/>}/>
                        <Route path="/settings/profile" element={<ProfilePage/>}/>
                        <Route path="/settings/mfa-setup" element={<MfaSetup/>}/>
                        <Route path="/settings/import" element={<ImportPage/>}/>
                        {featureFlags.enableThirdPartyIntegration && (
                            <>
                                <Route path="/sales-tracker" element={<ForecastOutputsSales/>}/>
                                <Route path="/settings/integrations" element={<ThirdPartyIntegrationsPage/>}/>
                                <Route path="/settings/synchronization" element={<SynchronizationPage/>}/>
                            </>
                        )}
                        
                        {/* Redirects for old URLs */}
                        <Route path="/products-table" element={<Navigate to="/products" replace />}/>
                        <Route path="/customers-table" element={<Navigate to="/customers" replace />}/>
                        <Route path="/customer-forecast/:id?" element={<Navigate to="/forecasting" replace />}/>
                        <Route path="/outputs/forecast-outputs" element={<Navigate to="/summary" replace />}/>
                        <Route path="/outputs/forecast-outputs-account" element={<Navigate to="/p-and-l" replace />}/>
                        <Route path="/outputs/forecast-outputs-promotions" element={<Navigate to="/promotion-laydown" replace />}/>
                        <Route path="/outputs/forecast-accuracy" element={<Navigate to="/forecast-accuracy" replace />}/>
                        {featureFlags.enableThirdPartyIntegration && (
                            <Route path="/outputs/forecast-outputs-sales" element={<Navigate to="/sales-tracker" replace />}/>
                        )}
                    </Route>
                    {/* Admin Routes */}
                    <Route element={<ProtectedRoute isAdminRequired={true}/>}>
                        <Route path="/admin" element={<AdminPage/>}/>
                        <Route path="/register" element={<RegisterPage/>}/>
                    </Route>
                </Routes>
            </DashboardLayout>
        </AppProvider>
    );
}

function App() {

    useEffect(() => {
        const gaMeasurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;

        if (window.location.hostname === 'abakusiq.com' && gaMeasurementId) {
            ReactGA.initialize(gaMeasurementId);
        }
    }, []);

    return (
        <AuthProvider>
            <Routes>
                <Route path="*" element={<AppWithNavigation/>}/>
            </Routes>
        </AuthProvider>
    );
}

export default App;

